import { ArrowBackIos } from '@finn/design-system/icons/arrow-back-ios';
import { ArrowForwardIos } from '@finn/design-system/icons/arrow-forward-ios';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { Image } from '@finn/ui-components';
import { cn } from '@finn/ui-utils';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

type Props = {
  imageUrls: (string | undefined)[];
  showDisclaimer?: boolean;
  disclaimerText: string;
  hasLifestyleImage: boolean;
  altText?: string;
};

export const ImageGalleryDesktopV2 = ({
  imageUrls,
  showDisclaimer,
  disclaimerText,
  altText = 'car',
  hasLifestyleImage,
}: Props) => {
  const searchParams = useSearchParams();
  const startIndex = searchParams.get('image');
  const [imageIndex, setImageIndex] = useState(0);
  useEffect(() => {
    if (!startIndex) {
      return;
    }

    setImageIndex(parseInt(startIndex) - (hasLifestyleImage ? 0 : 1));
  }, [startIndex, hasLifestyleImage]);

  const setNextImageIdx = (nextIndex: number, control: string) => () => {
    const idx =
      nextIndex < 0 ? imageUrls.length - 1 : nextIndex % imageUrls.length;
    interactionTrackingEvent(TrackingEventName.GALLERY_CONTROL_ACTION, {
      control,
      prevIndex: imageIndex + 1,
      newIndex: idx + 1,
    });
    setImageIndex(idx);
  };

  return (
    <>
      <div className={'flex flex-col-reverse gap-4'}>
        <div className="focus-visible: relative col-start-1 col-end-3 flex h-[460px] w-full overflow-hidden rounded-lg bg-snow p-0">
          {showDisclaimer && (
            <div
              className={cn(
                'absolute bottom-0 mb-1 w-full py-4 pl-4 pr-20 text-right body-12-regular'
              )}
            >
              {disclaimerText}
            </div>
          )}
          <div className="absolute left-4 top-1/2 z-overlay flex -translate-y-1/2 gap-1 rounded-md px-2 py-3 body-12-medium">
            <div
              className="cursor-pointer rounded-full border border-pearl bg-white p-3 hover:bg-snow"
              onClick={setNextImageIdx(imageIndex - 1, 'arrow-left')}
            >
              <ArrowBackIos />
            </div>
          </div>
          <div className="absolute right-4 top-1/2 z-overlay flex -translate-y-1/2 gap-1 rounded-md px-2 py-3 body-12-medium">
            <div
              className="cursor-pointer rounded-full border border-pearl bg-white p-3 hover:bg-snow"
              onClick={setNextImageIdx(imageIndex + 1, 'arrow-right')}
            >
              <ArrowForwardIos />
            </div>
          </div>
          <div className="relative flex size-full items-center justify-center">
            {imageUrls.map((_, index) => (
              <Image
                key={index}
                draggable={false}
                className={cn(
                  'absolute h-full max-h-[460px] w-auto max-w-full select-none object-cover opacity-0 mix-blend-multiply',
                  index === imageIndex && 'opacity-100'
                )}
                src={imageUrls[index]}
                alt={altText}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
